/**
 * FloatingFeedbackTab — sticky right-edge "Palaute" pill.
 *
 * Mounted once by Layout on eligible member routes. Messages, benefits,
 * admin/CMS and authentication surfaces are excluded by the shell.
 *
 * Behaviour: the pill is a `<button>` that opens a controlled
 * `FeedbackModal`. Open-state lives in local `useState` rather than a
 * route param or global store — the pill and the modal are a single
 * shell widget, and a parent store would over-couple the two.
 * The modal owns its internal form state; reset between sessions is
 * structural via an **open-session counter** key. The counter
 * increments only on the closed→open transition (the pill click and
 * the `onOpenChange` transition to `true`), never on close. Keying on
 * the `open` boolean instead would remount on CLOSE, unmounting the
 * dialog before Radix `react-presence` reaches `data-state="closed"`
 * — the close keyframes in `shell.css` would never play and the modal
 * would snap out instantly. With the counter, closing keeps the same
 * instance mounted so Radix animates the exit; the next open bumps the
 * counter and remounts a fresh `FeedbackModal` with `useState`
 * defaults (B-INV-001 "fresh internal state every open", R10). The
 * increment is effect-free — it happens in the open handler, NOT in a
 * reset `useEffect` (R11; the prop-sync reset Effect was the
 * anti-pattern this idiom replaces). This component owns only the
 * open/closed boolean and the open-session counter.
 *
 * Visual treatment:
 *   - Fixed position, anchored `right-0` with `bottom-32` (8rem) so
 *     it sits well above the bottom-nav (4.5rem `--spacing-bottom-nav`).
 *   - Vertical text via `writing-mode: vertical-rl` + `rotate-180`,
 *     so the label reads bottom-to-top with the natural reading
 *     direction once the user turns their head right.
 *   - Background `bg-brand-teal` — medium teal-green, distinct from
 *     the FAB's brand-deep so the two floating affordances don't
 *     compete for attention.
 *   - `rounded-l-md` (only the left edge is rounded; the right edge
 *     is flush with the viewport).
 *   - Tap area meets FR-036 (44×44 minimum) via `min-h-11` /
 *     `w-feedback-tab` on the button element. The visible pill is wider/taller
 *     by design (40×85 in the proto).
 */

import { type JSX, useRef, useState } from 'react';

import { useTranslate } from '../i18n/useTranslate.js';
import { FeedbackModal } from './FeedbackModal.js';

const tabClasses =
  'fixed right-0 bottom-32 z-30 inline-flex min-h-11 w-feedback-tab items-center justify-center ' +
  'rounded-l-md bg-brand-teal px-2 py-4 text-sm font-semibold tracking-wide ' +
  'text-brand-teal-foreground shadow-lg cursor-pointer transition-colors ' +
  'hover:bg-brand-teal/90 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
const tabLabelClasses = 'inline-block rotate-180 [writing-mode:vertical-rl]';

export function FloatingFeedbackTab(): JSX.Element {
  const t = useTranslate();
  const label = t('home.feedback.label');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  // Open-session counter — bumped only on closed→open so the modal key
  // changes on open (fresh instance) but NOT on close (same instance
  // stays mounted so Radix react-presence can animate the exit).
  const [openSession, setOpenSession] = useState(0);

  // Single open-intent path: opens the modal AND bumps the session
  // counter in the same handler. The functional updater keeps the
  // increment correct under React 18+ batching / StrictMode.
  const openModal = (): void => {
    setOpen(true);
    setOpenSession((session) => session + 1);
  };

  const handleOpenChange = (next: boolean): void => {
    if (next) {
      openModal();
      return;
    }
    setOpen(false);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openModal}
        className={tabClasses}
        aria-label={label}
      >
        <span aria-hidden="true" className={tabLabelClasses}>
          {label}
        </span>
      </button>
      <FeedbackModal
        key={openSession}
        open={open}
        onOpenChange={handleOpenChange}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          triggerRef.current?.focus();
        }}
      />
    </>
  );
}
